import { Link } from "react-router-dom";
import type { Article } from "@/types/news";
import { formatDate, formatViews, getCategoryName } from "@/utils/articles";

interface NewsCardProps {
  article: Article;
  variant?: "grid" | "list" | "mini";
}

export default function NewsCard({ article, variant = "grid" }: NewsCardProps) {
  const dateLabel = formatDate(article.publishedAt).slice(5);

  if (variant === "mini") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-start gap-3 py-3 cursor-pointer"
      >
        <div className="w-[88px] h-[66px] shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={`${article.title}配图`}
            title={`${article.title} - 51黑料网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-sm font-bold leading-snug text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-[11px] text-foreground-500 font-label">
            {dateLabel} · {formatViews(article.views)} 阅读
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-3 md:gap-5 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-[118px] h-[88px] md:w-[240px] md:h-[154px] shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.image}
            alt={`${article.title}配图`}
            title={`${article.title} - 51黑料网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 font-medium">
              {getCategoryName(article.category)}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800">
              {article.region}
            </span>
            {article.featured && (
              <span className="text-[11px] px-2 py-0.5 rounded-md bg-accent-100 text-accent-900">
                独家
              </span>
            )}
          </div>
          <h3 className="mt-2 font-heading text-base md:text-xl font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="hidden sm:block mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
            {article.summary}
          </p>
          <div className="mt-auto pt-2 md:pt-3 flex items-center gap-3 text-[11px] md:text-xs text-foreground-500 flex-wrap">
            <span>{article.author}</span>
            <span className="w-px h-3 bg-background-300" />
            <span className="font-label">{formatDate(article.publishedAt)}</span>
            <span className="hidden md:inline-flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line text-xs" aria-hidden="true" />
              </span>
              {formatViews(article.views)}
            </span>
            <span className="hidden md:inline-flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-chat-3-line text-xs" aria-hidden="true" />
              </span>
              {article.commentCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={article.image}
          alt={`${article.title}配图`}
          title={`${article.title} - 51黑料网`}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 px-2 py-0.5 rounded-md bg-primary-600 text-background-50 text-[11px] font-medium">
          {getCategoryName(article.category)}
        </span>
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <h3 className="font-heading text-base md:text-[17px] font-bold leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
          {article.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-[11px] text-foreground-500">
          <span className="truncate">{article.author}</span>
          <span className="flex items-center gap-2 shrink-0 font-label">
            <span>{dateLabel}</span>
            <span className="w-px h-3 bg-background-300" />
            <span className="inline-flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line" aria-hidden="true" />
              </span>
              {formatViews(article.views)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}